/*
# gamepad-svg

Generates a gamepad SVG using tosijs element creators. All colors and stroke
width are configurable via a defaults object. Elements use `data-part` attributes
(not IDs) so multiple instances can coexist.

## Color variables

| Variable | Default | Used by |
|----------|---------|---------|
| `strokeColor` | `#000000` | All stroked elements |
| `strokeWidth` | `16` | All stroked elements |
| `widgetColor` | `#ffffff` | Bumpers, triggers, dpad, sticks, menu, view |
| `controllerColor` | `#aaaaaa` | Controller body |
| `stickTravelColor` | `#505050` | Stick travel circles |
| `fillA` | `#ff1d25` | A button (red) |
| `fillB` | `#fcee22` | B button (yellow) |
| `fillX` | `#8cc63f` | X button (green) |
| `fillY` | `#3ea9f5` | Y button (blue) |

## Usage

```typescript
import { gamepadSvg } from 'tosijs-3d'

// Full gamepad with default colors
const pad = gamepadSvg()

// Custom colors
const dark = gamepadSvg({ controllerColor: '#333', widgetColor: '#888' })
```
*/

import { svgElements } from 'tosijs'

const { svg, path } = svgElements

const DEFAULTS = {
  strokeColor: '#000000',
  strokeWidth: '16',
  widgetColor: '#ffffff',
  controllerColor: '#aaaaaa',
  stickTravelColor: '#505050',
  fillA: '#ff1d25',
  fillB: '#fcee22',
  fillX: '#8cc63f',
  fillY: '#3ea9f5',
}

export type GamepadSvgColors = Partial<typeof DEFAULTS>

// Shared stroke props (reused by most elements)
function stroked(c: Required<typeof DEFAULTS>) {
  return {
    stroke: c.strokeColor,
    'stroke-width': c.strokeWidth,
    'stroke-linecap': 'butt',
    'stroke-linejoin': 'miter',
    'stroke-miterlimit': '10',
    'stroke-opacity': '1',
  } as Record<string, string>
}

// Common fill props
function filled(fill: string) {
  return {
    fill,
    'fill-opacity': '1',
    'fill-rule': 'evenodd',
    opacity: '1',
  } as Record<string, string>
}

export function gamepadSvg(colors: GamepadSvgColors = {}): SVGSVGElement {
  const c = { ...DEFAULTS, ...colors } as Required<typeof DEFAULTS>
  const s = stroked(c)

  const pad = svg(
    { viewBox: '0 185 1024 670' },
    // Controller body
    path({
      'data-part': 'controller',
      d: 'M512,272.739 C512,272.739,289.825,272.739,289.825,272.739 C238.158,272.739,191.828,304.565,173.29,352.792 C173.29,352.792,59.7954,648.041,59.7954,648.041 C34.5943,713.6,66.8499,787.23,132.127,813.153 C132.127,813.153,132.245,813.2,132.245,813.2 C190.148,836.194,256.199,814.221,288.789,761.122 C288.789,761.122,325.212,701.778,325.212,701.778 C332.194,690.402,344.583,683.47,357.93,683.47 C357.93,683.47,512,683.47,512,683.47 C512,683.47,666.07,683.47,666.07,683.47 C679.417,683.47,691.806,690.403,698.788,701.778 C698.788,701.778,735.211,761.123,735.211,761.123 C767.8,814.221,833.852,836.194,891.755,813.2 C891.755,813.2,891.873,813.153,891.873,813.153 C957.15,787.23,989.406,713.601,964.205,648.042 C964.205,648.042,850.71,352.792,850.71,352.792 C832.171,304.565,785.842,272.739,734.174,272.739 C734.174,272.739,512,272.739,512,272.739 z',
      ...filled(c.controllerColor),
      ...s,
    }),
    // Bumpers
    path({
      'data-part': 'right_bumper',
      d: 'M810.891,299.09 C789.431,282.38,762.611,272.739,734.174,272.739 C734.174,272.739,632.853,272.739,632.853,272.739 C632.853,272.739,632.853,304.739,632.853,304.739 C632.853,304.739,734.174,304.739,734.174,304.739 C755.571,304.729,775.763,312.087,791.828,324.808 C791.828,324.808,810.891,299.09,810.891,299.09 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'left_bumper',
      d: 'M214.027,299.09 C235.487,282.38,262.307,272.739,290.744,272.739 C290.744,272.739,392.065,272.739,392.065,272.739 C392.065,272.739,392.065,304.739,392.065,304.739 C392.065,304.739,290.744,304.739,290.744,304.739 C269.347,304.729,249.155,312.087,233.09,324.808 C233.09,324.808,214.027,299.09,214.027,299.09 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    // D-pad
    path({
      'data-part': 'dpad_up',
      d: 'M273.494,353.055 C273.494,347.635,277.888,343.241,283.308,343.241 C283.308,343.241,303.229,343.241,303.229,343.241 C308.649,343.241,313.043,347.635,313.043,353.055 C313.043,353.055,313.043,390.31,313.043,390.31 C313.043,399.005,305.995,406.053,297.3,406.053 C297.3,406.053,289.237,406.053,289.237,406.053 C280.542,406.053,273.494,399.005,273.494,390.31 C273.494,390.31,273.494,353.055,273.494,353.055 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'dpad_down',
      d: 'M313.043,507.911 C313.043,513.331,308.649,517.725,303.229,517.725 C303.229,517.725,283.308,517.725,283.308,517.725 C277.888,517.725,273.494,513.331,273.494,507.911 C273.494,507.911,273.494,470.656,273.494,470.656 C273.494,461.961,280.542,454.912,289.237,454.912 C289.237,454.912,297.3,454.912,297.3,454.912 C305.995,454.912,313.043,461.961,313.043,470.656 C313.043,470.656,313.043,507.911,313.043,507.911 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'dpad_right',
      d: 'M370.696,410.708 C376.117,410.708,380.511,415.102,380.511,420.523 C380.511,420.523,380.511,440.443,380.511,440.443 C380.511,445.864,376.117,450.258,370.696,450.258 C370.696,450.258,333.441,450.258,333.441,450.258 C324.746,450.258,317.698,443.209,317.698,434.515 C317.698,434.515,317.698,426.451,317.698,426.451 C317.698,417.757,324.746,410.708,333.441,410.708 C333.441,410.708,370.696,410.708,370.696,410.708 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'dpad_left',
      d: 'M215.84,450.258 C210.42,450.258,206.026,445.864,206.026,440.443 C206.026,440.443,206.026,420.523,206.026,420.523 C206.026,415.102,210.42,410.708,215.84,410.708 C215.84,410.708,253.096,410.708,253.096,410.708 C261.79,410.708,268.839,417.757,268.839,426.451 C268.839,426.451,268.839,434.515,268.839,434.515 C268.839,443.209,261.79,450.258,253.096,450.258 C253.096,450.258,215.84,450.258,215.84,450.258 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    // Center buttons
    path({
      'data-part': 'menu',
      d: 'M544.182,406.884 C538.761,406.884,534.367,402.49,534.367,397.07 C534.367,397.07,534.367,377.149,534.367,377.149 C534.367,371.728,538.761,367.334,544.182,367.334 C544.182,367.334,581.437,367.334,581.437,367.334 C590.132,367.334,597.18,374.383,597.18,383.078 C597.18,383.078,597.18,391.141,597.18,391.141 C597.18,399.836,590.132,406.884,581.437,406.884 C581.437,406.884,544.182,406.884,544.182,406.884 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'view',
      d: 'M481.059,367.334 C486.479,367.334,490.873,371.728,490.873,377.149 C490.873,377.149,490.873,397.07,490.873,397.07 C490.873,402.49,486.479,406.884,481.059,406.884 C481.059,406.884,443.804,406.884,443.804,406.884 C435.109,406.884,428.06,399.836,428.06,391.141 C428.06,391.141,428.06,383.078,428.06,383.078 C428.06,374.383,435.109,367.334,443.804,367.334 C443.804,367.334,481.059,367.334,481.059,367.334 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    // Stick travel areas (no stroke)
    path({
      'data-part': 'left_stick_travel',
      d: 'M399.446,523.725 C437.506,523.725,468.36,554.58,468.36,592.64 C468.36,630.7,437.506,661.554,399.446,661.554 C361.385,661.554,330.531,630.7,330.531,592.64 C330.531,554.58,361.385,523.725,399.446,523.725 z',
      ...filled(c.stickTravelColor),
      stroke: 'none',
    }),
    path({
      'data-part': 'left_stick',
      d: 'M399.446,559.676 C417.652,559.676,432.41,574.434,432.41,592.64 C432.41,610.846,417.652,625.604,399.446,625.604 C381.24,625.604,366.481,610.846,366.481,592.64 C366.481,574.434,381.24,559.676,399.446,559.676 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'right_stick_travel',
      d: 'M624.554,523.726 C662.615,523.726,693.469,554.58,693.469,592.64 C693.469,630.7,662.615,661.554,624.554,661.554 C586.494,661.554,555.64,630.7,555.64,592.64 C555.64,554.58,586.494,523.726,624.554,523.726 z',
      ...filled(c.stickTravelColor),
      stroke: 'none',
    }),
    path({
      'data-part': 'right_stick',
      d: 'M624.554,559.676 C642.76,559.676,657.518,574.434,657.518,592.64 C657.518,610.846,642.76,625.604,624.554,625.604 C606.348,625.604,591.59,610.846,591.59,592.64 C591.59,574.434,606.348,559.676,624.554,559.676 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    // Face buttons (unique colors)
    path({
      'data-part': 'A',
      d: 'M726.435,457.355 C744.64,457.355,759.399,472.113,759.399,490.319 C759.399,508.525,744.64,523.283,726.435,523.283 C708.229,523.283,693.47,508.525,693.47,490.319 C693.47,472.113,708.229,457.355,726.435,457.355 z',
      ...filled(c.fillA),
      ...s,
    }),
    path({
      'data-part': 'Y',
      d: 'M726.435,336.12 C744.64,336.12,759.399,350.879,759.399,369.084 C759.399,387.29,744.64,402.049,726.435,402.049 C708.229,402.049,693.47,387.29,693.47,369.084 C693.47,350.879,708.229,336.12,726.435,336.12 z',
      ...filled(c.fillY),
      ...s,
    }),
    path({
      'data-part': 'X',
      d: 'M698.782,429.702 C698.782,447.907,684.023,462.666,665.817,462.666 C647.612,462.666,632.853,447.907,632.853,429.702 C632.853,411.496,647.612,396.737,665.817,396.737 C684.023,396.737,698.782,411.496,698.782,429.702 z',
      ...filled(c.fillX),
      ...s,
    }),
    path({
      'data-part': 'B',
      d: 'M820.016,429.702 C820.016,447.907,805.258,462.666,787.052,462.666 C768.846,462.666,754.088,447.907,754.088,429.702 C754.088,411.496,768.846,396.737,787.052,396.737 C805.258,396.737,820.016,411.496,820.016,429.702 z',
      ...filled(c.fillB),
      ...s,
    }),
    // Triggers
    path({
      'data-part': 'right_trigger',
      d: 'M787.578,262.926 C787.578,262.926,787.578,211.02,787.578,211.02 C787.578,205.265,782.396,200.897,776.724,201.872 C761.736,204.447,751.774,208.701,724.124,208.701 C697.33,208.701,686.513,204.447,671.525,201.872 C665.853,200.897,660.671,205.265,660.671,211.02 C660.671,211.02,660.671,252.739,660.671,252.739 C660.671,252.739,738.174,252.739,738.174,252.739 C755.528,252.739,772.28,256.33,787.578,262.926 z',
      ...filled(c.widgetColor),
      ...s,
    }),
    path({
      'data-part': 'left_trigger',
      d: 'M236.624,262.926 C236.624,262.926,236.624,211.02,236.624,211.02 C236.624,205.265,241.806,200.897,247.478,201.872 C262.466,204.447,272.428,208.701,300.078,208.701 C326.872,208.701,337.689,204.447,352.677,201.872 C358.349,200.897,363.531,205.265,363.531,211.02 C363.531,211.02,363.531,252.739,363.531,252.739 C363.531,252.739,286.028,252.739,286.028,252.739 C268.674,252.739,251.922,256.33,236.624,262.926 z',
      ...filled(c.widgetColor),
      ...s,
    })
  ) as SVGSVGElement
  // pointer-events:auto defends against a host's global `svg { pointer-events:
  // none }` (inherited). user-select/tap-highlight off kills the blue selection
  // flash on press+drag. Set via setAttribute — svgElements types style as an object.
  pad.setAttribute(
    'style',
    'pointer-events:auto;user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent'
  )
  return pad
}
